import React, { FC, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import axios from 'axios';
import Image from 'next/image';
import classNames from 'classnames/bind';
import service from 'error-handler/functions/service';
import { apiMap } from 'api/apiMap';
import useRefEffect from 'hooks/useRefEffect';
import { processDate } from 'sections/Blog/utils';
import { Icons } from 'sections/Blog/components/svg';
import styles from 'sections/Blog/components/SectionPosts/Post/Post.module.scss';

const cn = classNames.bind(styles);

interface PostProps {
  post: any;
};

const Post: FC<PostProps> = ({ post }): React.ReactElement => {
  const [{ likes }, set] = useCookies(['likes']);
  const [like, setLike] = useState<boolean>(false);
  const [countLike, setCountLike] = useState<number>(post.likes);

  useEffect(() => {
    try {
      if (likes[post.id]) setLike(true);
    } catch (err) { }
  }, []);

  const likeHandler = async () => {
    if (!like) {
      set('likes', { ...likes, [post.id]: true }, { path: '/' });
      setLike(true);
      setCountLike(countLike + 1);
      await axios.post(apiMap.topArticles.addLike, { id: post.id });
    }
  };

  const handleLike = () => service()('likeHandler')(likeHandler);

  const progress = useRef<HTMLDivElement>(null)

  const content = useRefEffect((container: HTMLDivElement) => {
    if (!container) return;
    const height = container.clientHeight;
    const topHeight = container.offsetTop;
    const clientHeight = window.innerHeight;

    const scrollEvent = () => {
      const scroll = window.scrollY - topHeight;
      const percent = (clientHeight + scroll) / (height / 100);

      if (percent > 100) return
      if (progress.current) progress.current.style.width = `${percent}%`;
    }
    scrollEvent()

    window.addEventListener('scroll', scrollEvent);

    return () => {
      window.removeEventListener('scroll', scrollEvent);
    }
  }, [])

  return (
    <div className={cn('post')}>
      {/* POST - HEADER */}
      <div className={cn('post__header')}>
        <div className={cn('container')}>
          <div className={cn('post__header-info')}>
            {post?.image && (
              <Image
                className={cn('post__header-image')}
                src={post?.image}
                alt={post.title}
                width={1170}
                height={700}
                priority={true}
              />
            )}

            <h1 className={cn('post__header-heading')}>{post?.title}</h1>

            <div className={cn('post__header-bottom')}>
              <div className={cn('post__header-data')}>
                <p className={cn('post__header-data-container')}>
                  <span>{processDate(post.updated_at)}</span>
                </p>
                <p className={cn('post__header-data-container')}>
                  {Icons.clock({})}
                  <span>{post.read} min read</span>
                </p>
              </div>

              <div className={cn('post__header-statistics')}>
                <div className={cn('post__header-views')}>
                  {Icons.eye({})}
                  <span>{post?.views || 0}</span>
                </div>

                <div className={cn('post__header-likes')}>
                  {Icons.heart({
                    handler: handleLike,
                    options: {
                      style: {
                        cursor: 'pointer',
                        fill: like ? 'tomato' : 'currentColor'
                      },
                    }
                  })}
                  <span>{countLike || 0}</span>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={cn('post__progress')}>
        <div ref={progress} className={cn('post__progress-element')}></div>
      </div>

      {/* POST - CONTENT */}
      <div className={cn('container')}>
        <div
          ref={content}
          className={cn('post__content')}
          dangerouslySetInnerHTML={{ __html: post.content.replaceAll('<img', '<img loading="lazy" ') }}
        />
      </div>

      {/* POST AUTHOR */}
      <div className={cn('post__author')}>
        <div className={cn('container')}>

          <div className={cn('post__author-content')}>

            <div className={cn('post__author-info')}>
              <div className={cn('post__author-credentials')}>
                <div className={cn('post__author-image-container')}>
                  {post?.author?.image && (
                    <Image
                      width={70}
                      height={70}
                      className={cn('post__author-image')}
                      src={post?.author?.image}
                      alt="Author avatar"
                    />
                  )}
                </div>
                <span>{post?.author?.full_name}</span>
              </div>

              <div className={cn('post__author-social')}>
                <a target="_blank" href="https://www.facebook.com/DigitalFreightAlliance_DPWorld">
                  {Icons.facebook({})}
                </a>

                <a target="_blank" href="https://x.com/i/flow/login?redirect_after_login=%2FD_F_Alliance">
                  {Icons.x({})}
                </a>

                <a target="_blank" href="https://www.linkedin.com/company/digital-freight-alliance/posts/?feedView=all">
                  {Icons.linkedin({})}
                </a>
              </div>
            </div>

            <div className={cn('post__author-description')} dangerouslySetInnerHTML={{ __html: post?.author?.description }} />
          </div>

        </div>
      </div>
    </div>
  );
};

export default Post;
